/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useCallback, useEffect, useState } from 'react'

import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import Skeleton from '@mui/material/Skeleton'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Collapse from '@mui/material/Collapse'
import Tooltip from '@mui/material/Tooltip'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'

import { useBoolean } from 'src/hooks/use-boolean'
import useDebounce from 'src/hooks/use-debounce'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import DateTimeRangePicker from 'src/components/date-time-range-picker'
import { useSnackbar } from 'src/components/snackbar'
import {
  BILLING_MODEL_LABELS,
  BillingModel,
  COST_CATEGORY_LABELS,
  DreData,
  DreEventRow,
  FinanceEvent,
  FinanceOperator,
  fetchDre,
  fetchFinanceEvents,
  fetchOperators,
  updateEventFinanceConfig,
} from 'src/service/finance.service'

import { formatCurrency } from '../orders/format-currency'

const PAYMENT_LABELS: Record<string, string> = {
  money: 'Dinheiro',
  credit: 'Crédito',
  debit: 'Débito',
  pix: 'Pix',
  card: 'CashLess',
}

const fmtPercent = (value: number) => `${(value || 0).toFixed(1).replace('.', ',')}%`

const parseDecimal = (value: string) => {
  const parsed = Number(String(value).replace(/\./g, '').replace(',', '.'))
  return Number.isNaN(parsed) ? 0 : parsed
}

type EventOption = { value: string; label: string; date?: string }

export default function DreTab() {
  const isLoading = useBoolean(false)

  const [allEvents, setAllEvents] = useState(false)
  const [selected, setSelected] = useState<EventOption[]>([])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [data, setData] = useState<DreData | null>(null)
  const [configEvent, setConfigEvent] = useState<DreEventRow | null>(null)

  const [eventSearch, setEventSearch] = useState('')
  const [eventOptions, setEventOptions] = useState<EventOption[]>([])
  const eventSearchDebounce = useDebounce(eventSearch, 400)

  useEffect(() => {
    let ignore = false
    fetchFinanceEvents(eventSearchDebounce).then((events: FinanceEvent[]) => {
      if (ignore) return
      setEventOptions(events.map((e) => ({ value: e._id, label: e.name, date: e.date })))
    })
    return () => {
      ignore = true
    }
  }, [eventSearchDebounce])

  const loadDre = useCallback(async () => {
    if (!allEvents && !selected.length) {
      setData(null)
      return
    }
    try {
      isLoading.onTrue()
      const response = await fetchDre({
        all: allEvents,
        events: selected.map((s) => s.value),
        startDate,
        endDate,
      })
      setData(response)
    } catch {
      setData(null)
    } finally {
      isLoading.onFalse()
    }
  }, [allEvents, selected, startDate, endDate])

  useEffect(() => {
    loadDre()
  }, [allEvents, selected, startDate, endDate])

  const totals = data?.totals
  const showEmptyState = !allEvents && !selected.length

  let eventsPlaceholder = 'Selecione os eventos do DRE...'
  if (allEvents) eventsPlaceholder = 'Todos os eventos'
  else if (selected.length) eventsPlaceholder = ''

  return (
    <Stack spacing={3}>
      <Card sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={1}
          >
            <FormControlLabel
              control={
                <Switch checked={allEvents} onChange={(e) => setAllEvents(e.target.checked)} />
              }
              label='Consolidar todos os eventos'
            />
            {allEvents && (
              <Typography variant='caption' color='text.secondary'>
                Reúne todos os eventos com vendas, cobrança por máquina ou custos no período.
                {' '}Use o período para focar e deixar o cálculo mais rápido.
              </Typography>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <Autocomplete
              multiple
              size='small'
              disabled={allEvents}
              options={eventOptions}
              value={selected}
              onChange={(_e, value) => setSelected(value)}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') setEventSearch(value)
                else if (reason === 'clear' || reason === 'reset') setEventSearch('')
              }}
              // Busca é server-side: não deixa o MUI re-filtrar a lista pelo texto do input.
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Eventos'
                  placeholder={eventsPlaceholder}
                />
              )}
              sx={{ flex: 1 }}
            />
            <Box sx={{ width: { xs: 1, md: 280 } }}>
              <DateTimeRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start)
                  setEndDate(end)
                }}
                size='small'
                label=''
                placeholder='Todo o período'
              />
            </Box>
          </Stack>
        </Stack>
      </Card>

      {showEmptyState && (
        <Card sx={{ p: 5, borderRadius: 2, textAlign: 'center' }}>
          <Iconify
            icon='solar:document-text-bold-duotone'
            width={48}
            sx={{ color: 'text.disabled', mb: 1.5 }}
          />
          <Typography variant='subtitle1' fontWeight={700}>
            Selecione os eventos para gerar o DRE
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            O resultado cruza as taxas cobradas nos pedidos com o custo da operadora, receita e
            custo por máquina e os custos lançados.
          </Typography>
        </Card>
      )}

      {!showEmptyState && isLoading.value && <Skeleton variant='rounded' height={360} />}

      {!showEmptyState && !isLoading.value && data && totals && (
        <>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <KpiCard
                title='Receita (taxas + máquinas)'
                value={formatCurrency(totals.revenue)}
                icon='solar:dollar-minimalistic-bold'
                color='success'
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <KpiCard
                title='Custos totais'
                value={formatCurrency(totals.totalCosts)}
                icon='solar:calculator-minimalistic-bold'
                color='warning'
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <KpiCard
                title='Resultado'
                value={formatCurrency(totals.result)}
                icon='solar:wallet-money-bold'
                color={totals.result >= 0 ? 'primary' : 'error'}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <KpiCard
                title='Margem'
                value={fmtPercent(totals.margin)}
                icon='solar:chart-2-bold'
                color={totals.result >= 0 ? 'info' : 'error'}
              />
            </Grid>
          </Grid>

          <Card sx={{ borderRadius: 2 }}>
            <SectionHeader
              icon='solar:document-text-bold'
              title='DRE Consolidado'
              subtitle={
                data.scope === 'global'
                  ? `${data.events.length} evento(s) com atividade financeira`
                  : `${data.events.length} evento(s) selecionado(s)`
              }
            />
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                <DreLine
                  label='Receita de taxas (eventos por taxa)'
                  value={formatCurrency(totals.taxRevenue)}
                />
                <DreLine
                  label='Receita de máquinas (eventos por máquina)'
                  value={formatCurrency(totals.machineRevenue)}
                />
                <DreLine label='(=) Receita total' value={formatCurrency(totals.revenue)} strong />
                <Divider />
                <DreLine
                  label='(-) Custo de adquirência (operadora)'
                  value={`- ${formatCurrency(totals.acquirerCost)}`}
                  color='error.main'
                />
                <DreLine
                  label='(-) Custo das máquinas repassado'
                  value={`- ${formatCurrency(totals.machineCost)}`}
                  color='error.main'
                />
                <DreLine
                  label='(-) Custos lançados nos eventos'
                  value={`- ${formatCurrency(totals.entryCosts)}`}
                  color='error.main'
                />
                <DreLine
                  label='(-) Custos gerais do período'
                  value={`- ${formatCurrency(totals.generalCosts)}`}
                  color='error.main'
                />
                <DreLine
                  label='(=) Custos totais'
                  value={`- ${formatCurrency(totals.totalCosts)}`}
                  color='error.main'
                  strong
                />
                <Divider />
                <DreLine
                  label='(=) Resultado'
                  value={formatCurrency(totals.result)}
                  color={totals.result >= 0 ? 'success.dark' : 'error.main'}
                  strong
                />
              </Stack>
              {!data.defaultOperator && totals.taxRevenue > 0 && (
                <Alert severity='warning' sx={{ mt: 2, borderRadius: 2 }}>
                  Nenhuma operadora padrão cadastrada — o custo de adquirência está zerado para
                  eventos sem operadora definida. Cadastre suas taxas na aba “Taxas de
                  Operadoras”.
                </Alert>
              )}
            </Box>
          </Card>

          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              icon='solar:list-check-bold'
              title='Resultado por evento'
              subtitle='Clique em um evento para ver o detalhamento; use a engrenagem para configurar cobrança.'
            />
            <TableContainer>
              <Scrollbar>
                <Table size='small' sx={{ minWidth: 1080 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Evento</TableCell>
                      <TableCell>Modelo</TableCell>
                      <TableCell>Operadora</TableCell>
                      <TableCell align='right'>Transacionado</TableCell>
                      <TableCell align='right'>Receita</TableCell>
                      <TableCell align='right'>Custo operadora</TableCell>
                      <TableCell align='right'>Custo máquinas</TableCell>
                      <TableCell align='right'>Custos lançados</TableCell>
                      <TableCell align='right'>Resultado</TableCell>
                      <TableCell align='right'>Margem</TableCell>
                      <TableCell align='center' />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.events.map((row) => (
                      <DreEventTableRow
                        key={row.eventId}
                        row={row}
                        onConfig={() => setConfigEvent(row)}
                      />
                    ))}
                    {!data.events.length && (
                      <TableRow>
                        <TableCell colSpan={11} align='center' sx={{ py: 5 }}>
                          <Typography variant='body2' color='text.secondary'>
                            Nenhum evento com atividade financeira no período selecionado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>
          </Card>

          {data.generalCosts.items.length > 0 && (
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <SectionHeader
                icon='solar:banknote-bold'
                title='Custos gerais do período'
                subtitle='Custos sem evento vinculado, abatidos no consolidado.'
              />
              <TableContainer>
                <Table size='small'>
                  <TableBody>
                    {data.generalCosts.items.map((cost) => (
                      <TableRow key={cost._id}>
                        <TableCell>{cost.description}</TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            variant='soft'
                            label={COST_CATEGORY_LABELS[cost.category] || cost.category}
                          />
                        </TableCell>
                        <TableCell align='right' sx={{ color: 'error.main', fontWeight: 700 }}>
                          {formatCurrency(cost.value)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                        Total
                      </TableCell>
                      <TableCell align='right' sx={{ color: 'error.main', fontWeight: 700 }}>
                        {formatCurrency(data.generalCosts.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}

      <EventConfigDialog
        row={configEvent}
        onClose={() => setConfigEvent(null)}
        onSaved={() => {
          setConfigEvent(null)
          loadDre()
        }}
      />
    </Stack>
  )
}

// ----------------------------------------------------------------------

function DreEventTableRow({ row, onConfig }: { row: DreEventRow; onConfig: () => void }) {
  const [open, setOpen] = useState(false)

  const needsMachineConfig =
    row.billingModel === 'machine' && (!row.machineCount || !row.machinePrice)

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <TableCell>
          <Stack direction='row' alignItems='center' spacing={0.75}>
            <Iconify
              icon={open ? 'eva:arrow-down-fill' : 'eva:arrow-right-fill'}
              width={16}
              sx={{ color: 'text.disabled' }}
            />
            <Box>
              <Typography variant='body2' fontWeight={600}>
                {row.name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {row.local || 'Sem local'}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Chip
            size='small'
            variant='soft'
            color={row.billingModel === 'machine' ? 'info' : 'primary'}
            label={BILLING_MODEL_LABELS[row.billingModel]}
          />
          {needsMachineConfig && (
            <Tooltip title='Informe nº de máquinas e valor cobrado por máquina'>
              <Chip size='small' variant='soft' color='warning' label='Configurar' sx={{ ml: 0.5 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          {row.billingModel === 'machine' ? (
            <Typography variant='caption' color='text.secondary'>
              —
            </Typography>
          ) : (
            <Typography variant='caption'>
              {row.operator?.name || 'Sem operadora'}
              {row.operatorIsDefault && row.operator ? ' (padrão)' : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell align='right'>{formatCurrency(row.gross)}</TableCell>
        <TableCell align='right' sx={{ fontWeight: 700 }}>
          {formatCurrency(row.revenue)}
        </TableCell>
        <TableCell align='right' sx={{ color: row.costs.acquirer ? 'error.main' : 'text.secondary' }}>
          {row.costs.acquirer ? `- ${formatCurrency(row.costs.acquirer)}` : '—'}
        </TableCell>
        <TableCell align='right' sx={{ color: row.costs.machines ? 'error.main' : 'text.secondary' }}>
          {row.costs.machines ? `- ${formatCurrency(row.costs.machines)}` : '—'}
        </TableCell>
        <TableCell align='right' sx={{ color: row.costs.entries ? 'error.main' : 'text.secondary' }}>
          {row.costs.entries ? `- ${formatCurrency(row.costs.entries)}` : '—'}
        </TableCell>
        <TableCell align='right'>
          <Typography
            variant='body2'
            fontWeight={700}
            color={row.result >= 0 ? 'success.dark' : 'error.main'}
          >
            {formatCurrency(row.result)}
          </Typography>
        </TableCell>
        <TableCell align='right'>
          <Chip
            size='small'
            variant='soft'
            color={row.result >= 0 ? 'success' : 'error'}
            label={fmtPercent(row.margin)}
          />
        </TableCell>
        <TableCell align='center' onClick={(e) => e.stopPropagation()}>
          <Tooltip title='Configurar cobrança do evento'>
            <IconButton size='small' onClick={onConfig}>
              <Iconify icon='solar:settings-bold' width={16} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ py: 2, pl: 3.5 }}>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <Typography variant='caption' color='text.secondary' fontWeight={700}>
                    Faturamento por meio de pagamento
                  </Typography>
                  <Table size='small' sx={{ mt: 0.5 }}>
                    <TableBody>
                      {Object.entries(row.payments)
                        .filter(([, slot]) => slot.count > 0)
                        .map(([type, slot]) => (
                          <TableRow key={type}>
                            <TableCell sx={{ border: 'none', py: 0.25 }}>
                              {PAYMENT_LABELS[type] || type}
                            </TableCell>
                            <TableCell align='right' sx={{ border: 'none', py: 0.25 }}>
                              {slot.count}x
                            </TableCell>
                            <TableCell align='right' sx={{ border: 'none', py: 0.25 }}>
                              {formatCurrency(slot.gross)}
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{ border: 'none', py: 0.25, color: 'text.secondary' }}
                            >
                              taxa {formatCurrency(slot.tax)}
                            </TableCell>
                          </TableRow>
                        ))}
                      {!Object.values(row.payments).some((slot) => slot.count > 0) && (
                        <TableRow>
                          <TableCell sx={{ border: 'none', color: 'text.secondary' }}>
                            Sem vendas no período.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid xs={12} md={6}>
                  {row.billingModel === 'machine' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant='caption' color='text.secondary' fontWeight={700}>
                        Máquinas
                      </Typography>
                      <Typography variant='body2'>
                        {row.machineCount} máquina(s) × {formatCurrency(row.machinePrice)} ={' '}
                        <strong>{formatCurrency(row.machineRevenue)}</strong> de receita · custo{' '}
                        {formatCurrency(row.machineCostUnit)}/máquina ={' '}
                        <strong>{formatCurrency(row.costs.machines)}</strong>
                      </Typography>
                    </Box>
                  )}
                  <Typography variant='caption' color='text.secondary' fontWeight={700}>
                    Custos lançados neste evento
                  </Typography>
                  {row.costEntries.length ? (
                    <Table size='small' sx={{ mt: 0.5 }}>
                      <TableBody>
                        {row.costEntries.map((cost) => (
                          <TableRow key={cost._id}>
                            <TableCell sx={{ border: 'none', py: 0.25 }}>
                              {cost.description}
                            </TableCell>
                            <TableCell sx={{ border: 'none', py: 0.25 }}>
                              <Chip
                                size='small'
                                variant='soft'
                                label={COST_CATEGORY_LABELS[cost.category] || cost.category}
                              />
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{ border: 'none', py: 0.25, color: 'error.main' }}
                            >
                              {formatCurrency(cost.value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Nenhum custo lançado.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

// ----------------------------------------------------------------------

type ConfigFormState = {
  billingModel: BillingModel
  machineCount: string
  machinePrice: string
  machineCost: string
  operator: string
}

function EventConfigDialog({
  row,
  onClose,
  onSaved,
}: {
  row: DreEventRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const { enqueueSnackbar } = useSnackbar()
  const isSaving = useBoolean(false)
  const [operators, setOperators] = useState<FinanceOperator[]>([])
  const [form, setForm] = useState<ConfigFormState | null>(null)

  useEffect(() => {
    if (!row) {
      setForm(null)
      return
    }
    setForm({
      billingModel: row.billingModel,
      machineCount: row.machineCount ? String(row.machineCount) : '',
      machinePrice: row.machinePrice ? String(row.machinePrice / 100).replace('.', ',') : '',
      machineCost: row.machineCostUnit ? String(row.machineCostUnit / 100).replace('.', ',') : '',
      operator: row.operatorIsDefault ? '' : row.operator?._id || '',
    })
    fetchOperators().then(setOperators)
  }, [row])

  const handleSave = async () => {
    if (!row || !form) return
    try {
      isSaving.onTrue()
      await updateEventFinanceConfig(row.eventId, {
        billingModel: form.billingModel,
        machineCount: Math.max(parseInt(form.machineCount, 10) || 0, 0),
        machinePrice: Math.round(parseDecimal(form.machinePrice) * 100),
        machineCost: Math.round(parseDecimal(form.machineCost) * 100),
        operator: form.operator || null,
      })
      enqueueSnackbar('Configuração salva!')
      onSaved()
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.error || 'Erro ao salvar configuração.', {
        variant: 'error',
      })
    } finally {
      isSaving.onFalse()
    }
  }

  return (
    <Dialog open={!!row} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Cobrança — {row?.name}</DialogTitle>
      {form && (
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant='caption' color='text.secondary' fontWeight={600}>
                Modelo de cobrança
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size='small'
                value={form.billingModel}
                onChange={(_e, value) => {
                  if (value) setForm({ ...form, billingModel: value })
                }}
                sx={{ mt: 0.5 }}
              >
                <ToggleButton value='tax'>
                  <Iconify icon='carbon:percentage' width={16} sx={{ mr: 0.5 }} />
                  Por taxa
                </ToggleButton>
                <ToggleButton value='machine'>
                  <Iconify icon='solar:printer-minimalistic-linear' width={16} sx={{ mr: 0.5 }} />
                  Por máquina
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                {form.billingModel === 'tax'
                  ? 'Receita = taxas cobradas nos pedidos. O custo da operadora é abatido no DRE.'
                  : 'Receita = nº de máquinas × valor cobrado. O custo por máquina é repassado como custo no DRE (sem custo de operadora).'}
              </Typography>
            </Box>

            {form.billingModel === 'machine' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label='Nº de máquinas'
                  value={form.machineCount}
                  onChange={(e) => setForm({ ...form, machineCount: e.target.value })}
                  size='small'
                  fullWidth
                />
                <TextField
                  label='Cobrado por máquina'
                  value={form.machinePrice}
                  onChange={(e) => setForm({ ...form, machinePrice: e.target.value })}
                  size='small'
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                  }}
                />
                <TextField
                  label='Custo por máquina'
                  value={form.machineCost}
                  onChange={(e) => setForm({ ...form, machineCost: e.target.value })}
                  size='small'
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                  }}
                />
              </Stack>
            ) : (
              <TextField
                select
                label='Operadora (custo de adquirência)'
                value={form.operator}
                onChange={(e) => setForm({ ...form, operator: e.target.value })}
                size='small'
                fullWidth
                helperText='Sem seleção, usa a operadora padrão.'
              >
                <MenuItem value=''>Usar operadora padrão</MenuItem>
                {operators.map((operator) => (
                  <MenuItem key={operator._id} value={operator._id}>
                    {operator.name}
                    {operator.isDefault ? ' (padrão)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>
      )}
      <DialogActions>
        <Button color='inherit' onClick={onClose}>
          Cancelar
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={isSaving.value}>
          {isSaving.value ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ----------------------------------------------------------------------

function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: string
  title: string
  subtitle?: string
}) {
  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing={1.25}
      sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
    >
      <Iconify icon={icon} width={18} sx={{ color: 'primary.main' }} />
      <Box>
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant='caption' color='text.secondary'>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

function DreLine({
  label,
  value,
  color,
  strong,
}: {
  label: string
  value: string
  color?: string
  strong?: boolean
}) {
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography variant='body2' fontWeight={strong ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant='body2' fontWeight={strong ? 700 : 600} sx={{ color }}>
        {value}
      </Typography>
    </Stack>
  )
}

function KpiCard({
  title,
  value,
  icon,
  color,
}: {
  title: string
  value: string
  icon: string
  color: 'success' | 'warning' | 'primary' | 'info' | 'error'
}) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: `${color}.lighter` }}>
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary'>
            {title}
          </Typography>
          <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
